import { useCallback, useEffect, useRef, useState } from 'react';
import { daySequenceActions } from '@core/daySequence/daySequence.action';
import { clientActions } from '@core/client/client.action';
import { jobActions } from '@core/job/job.action';
import { userActions } from '@core/user/user.action';
import {
  type ConcurrencyLimiter,
  createConcurrencyLimiter,
  mapWithConcurrency,
} from '@core/util/concurrency.util';
import {
  type Client,
  type DaySequence,
  DaySequenceStatus,
  type Job,
  type User,
} from '@core/generated/gql-types';
import type { DaySequenceLookups } from '@/modules/payroll/utils/daySequenceGroups.utils';

const EMPTY_LOOKUPS: DaySequenceLookups = {
  workers: {},
  jobs: {},
  clients: {},
};

const uniqueIds = (ids: Array<string | null | undefined>): string[] =>
  Array.from(new Set(ids.filter((id): id is string => Boolean(id))));

/**
 * Fetches each id through the shared limiter so only a few requests are in
 * flight at once; ids that fail or are missing are omitted.
 */
const fetchByIds = async <T>(
  ids: string[],
  fetchOne: (id: string) => Promise<T | null | undefined>,
  limiter: ConcurrencyLimiter,
): Promise<Record<string, T>> => {
  const entries = await mapWithConcurrency(
    ids,
    async (id) => {
      try {
        return [id, await fetchOne(id)] as const;
      } catch {
        return [id, null] as const;
      }
    },
    limiter,
  );
  return entries.reduce<Record<string, T>>((acc, [id, value]) => {
    if (value) {
      acc[id] = value;
    }
    return acc;
  }, {});
};

export type PayrollDaySequencesState = {
  sequences: DaySequence[];
  lookups: DaySequenceLookups;
  isLoading: boolean;
  isLoadingDetails: boolean;
  error: string | null;
  lastSyncedAt: number | null;
  pendingStatusIds: Set<string>;
  refresh: () => Promise<void>;
  updateStatus: (
    sequence: DaySequence,
    status: DaySequenceStatus,
  ) => Promise<void>;
};

/**
 * Day sequences requiring action for the tenant, plus the workers, jobs and
 * clients they reference (fetched once per unique id).
 */
export const usePayrollDaySequences = (
  tenantId: string | null,
): PayrollDaySequencesState => {
  const [sequences, setSequences] = useState<DaySequence[]>([]);
  const [lookups, setLookups] = useState<DaySequenceLookups>(EMPTY_LOOKUPS);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
  const [pendingStatusIds, setPendingStatusIds] = useState<Set<string>>(
    () => new Set(),
  );
  const requestIdRef = useRef(0);

  const hydrateLookups = useCallback(async (rows: DaySequence[]) => {
    const requestId = requestIdRef.current;
    setIsLoadingDetails(true);
    // One limiter for workers, jobs and clients so they share the same cap.
    const limiter = createConcurrencyLimiter();
    try {
      const [workers, jobs, clients] = await Promise.all([
        fetchByIds<User>(
          uniqueIds(rows.map((row) => row.userId)),
          (id) => userActions.fetchUserById(id),
          limiter,
        ),
        fetchByIds<Job>(
          uniqueIds(rows.map((row) => row.jobId)),
          async (id) => (await jobActions.getJob(id))?.data?.getJob,
          limiter,
        ),
        fetchByIds<Client>(
          uniqueIds(rows.map((row) => row.clientId)),
          (id) => clientActions.getClientById(id),
          limiter,
        ),
      ]);
      if (requestId === requestIdRef.current) {
        setLookups({ workers, jobs, clients });
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoadingDetails(false);
      }
    }
  }, []);

  const refresh = useCallback(async () => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    if (!tenantId) {
      setSequences([]);
      setLookups(EMPTY_LOOKUPS);
      setLastSyncedAt(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const data =
        await daySequenceActions.getDaySequencesRequiringAction(tenantId);
      if (requestId !== requestIdRef.current) {
        return;
      }
      const rows = (data ?? []).filter(Boolean);
      setSequences(rows);
      setLastSyncedAt(Date.now());
      void hydrateLookups(rows);
    } catch (err) {
      if (requestId === requestIdRef.current) {
        setError(
          err instanceof Error ? err.message : 'Unable to load day sequences.',
        );
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [tenantId, hydrateLookups]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const updateStatus = useCallback(
    async (sequence: DaySequence, status: DaySequenceStatus) => {
      if (sequence.status === status) {
        return;
      }
      const mutate =
        status === DaySequenceStatus.ReadyForPay
          ? daySequenceActions.approveDaySequences
          : status === DaySequenceStatus.EscalatedToTenant
            ? daySequenceActions.disputeDaySequences
            : null;
      if (!mutate) {
        throw new Error(`Changing status to ${status} is not supported.`);
      }

      setPendingStatusIds((current) => new Set(current).add(sequence.id));
      try {
        const updated = (await mutate([sequence.id])).find(
          (item) => item?.id === sequence.id,
        );
        if (!updated) {
          // The core actions swallow errors and return []. Resync so the list
          // reflects the real server state, and surface the failure.
          await refresh();
          throw new Error('The status update was not applied.');
        }
        setSequences((previous) =>
          previous.map((item) => (item.id === updated.id ? updated : item)),
        );
      } finally {
        setPendingStatusIds((current) => {
          const next = new Set(current);
          next.delete(sequence.id);
          return next;
        });
      }
    },
    [refresh],
  );

  return {
    sequences,
    lookups,
    isLoading,
    isLoadingDetails,
    error,
    lastSyncedAt,
    pendingStatusIds,
    refresh,
    updateStatus,
  };
};
